/**
 * Security Test Results — admin-only detail view.
 *
 * Surfaces the authorization-boundary test suite that runs nightly at
 * 3 AM EDT (and on demand via the "Run Now" button). Provides:
 *   • Persistent badge at the top: green / amber / red based on the
 *     last run's pass/fail counts, with a "last verified" timestamp.
 *   • Per-test result list with expandable failure details.
 *   • Run history table (last 30 runs) so trends are visible.
 *   • Manual "Run Now" trigger for ad-hoc verification.
 *
 * Backed by:
 *   GET  /api/admin/security-tests/latest    → badge + current results
 *   GET  /api/admin/security-tests/history   → history table
 *   POST /api/admin/security-tests/run       → kick off ad-hoc run
 *
 * Auth: admin-only (super_user or admin role).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Shield, ShieldAlert, ShieldCheck, ChevronLeft,
  RefreshCw, Clock, CheckCircle2, XCircle, AlertTriangle, MinusCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_ICONS = {
  pass:  { Icon: CheckCircle2, color: 'text-emerald-600', label: 'pass' },
  fail:  { Icon: XCircle,      color: 'text-rose-600',    label: 'fail' },
  error: { Icon: AlertTriangle, color: 'text-amber-600',  label: 'error' },
  skip:  { Icon: MinusCircle,  color: 'text-slate-400',   label: 'skip' },
};

function formatTimestamp(iso) {
  if (!iso) return '—';
  try {
    // Format in the user's local timezone — Charlotte HQ is EDT/EST.
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
}

function StatusBanner({ run }) {
  if (!run || run.status === 'never_run') {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex items-center gap-3" data-testid="security-tests-banner-never">
        <Shield className="h-6 w-6 text-slate-400" />
        <div>
          <div className="font-semibold text-slate-900">No test run on record yet</div>
          <div className="text-sm text-slate-600">Click &ldquo;Run Now&rdquo; to perform the first authorization-boundary check.</div>
        </div>
      </div>
    );
  }
  const s = run.summary || {};
  const allPassed = s.failed === 0 && s.errored === 0 && s.total > 0;
  const anyError = s.errored > 0;
  const tone = allPassed
    ? { ring: 'border-emerald-200 bg-emerald-50', icon: ShieldCheck, color: 'text-emerald-600', headline: `All ${s.total} authorization checks passing` }
    : anyError
      ? { ring: 'border-rose-200 bg-rose-50', icon: ShieldAlert, color: 'text-rose-600', headline: `${s.errored} test(s) errored — investigate` }
      : { ring: 'border-amber-200 bg-amber-50', icon: ShieldAlert, color: 'text-amber-600', headline: `${s.failed} of ${s.total} checks failing` };
  const Icon = tone.icon;
  return (
    <div className={`rounded-lg border ${tone.ring} p-4 flex items-center gap-3`} data-testid="security-tests-banner">
      <Icon className={`h-6 w-6 ${tone.color}`} />
      <div className="flex-1">
        <div className="font-semibold text-slate-900">{tone.headline}</div>
        <div className="text-sm text-slate-600 flex items-center gap-2">
          <Clock className="h-3.5 w-3.5" />
          Last verified {formatTimestamp(run.finished_at)} ·
          {' '}{run.triggered_by === 'scheduled' ? 'nightly run' : 'manual run'} ·
          {' '}took {Math.round((run.duration_ms || 0) / 100) / 10}s
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Badge className="bg-emerald-100 text-emerald-700">{s.passed} passed</Badge>
        {s.failed > 0 && <Badge className="bg-rose-100 text-rose-700">{s.failed} failed</Badge>}
        {s.errored > 0 && <Badge className="bg-amber-100 text-amber-700">{s.errored} errored</Badge>}
      </div>
    </div>
  );
}

function TestList({ tests }) {
  if (!tests || tests.length === 0) {
    return <p className="text-sm text-slate-500 italic">No test data.</p>;
  }
  return (
    <ul className="divide-y divide-slate-200">
      {tests.map((t) => {
        const meta = STATUS_ICONS[t.status] || STATUS_ICONS.skip;
        const Icon = meta.Icon;
        return (
          <li key={t.name} className="py-3 flex items-start gap-3" data-testid={`security-test-result-${t.name}`}>
            <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${meta.color}`} />
            <div className="flex-1 min-w-0">
              <div className="font-mono text-sm text-slate-900 break-words">{t.name}</div>
              {t.docstring && <div className="text-xs text-slate-500 mt-0.5">{t.docstring}</div>}
              {t.status !== 'pass' && t.message && (
                <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 font-mono whitespace-pre-wrap">
                  {t.message}
                </div>
              )}
            </div>
            <div className="text-xs text-slate-400 flex-shrink-0">{t.duration_ms}ms</div>
          </li>
        );
      })}
    </ul>
  );
}

function HistoryTable({ runs }) {
  if (!runs || runs.length === 0) {
    return <p className="text-sm text-slate-500 italic">No prior runs recorded yet.</p>;
  }
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
          <tr>
            <th className="px-3 py-2 text-left">When</th>
            <th className="px-3 py-2 text-left">Trigger</th>
            <th className="px-3 py-2 text-right">Passed</th>
            <th className="px-3 py-2 text-right">Failed</th>
            <th className="px-3 py-2 text-right">Errored</th>
            <th className="px-3 py-2 text-right">Duration</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {runs.map((r) => {
            const s = r.summary || {};
            const ok = (s.failed || 0) === 0 && (s.errored || 0) === 0 && (s.total || 0) > 0;
            return (
              <tr key={r._id || r.run_id} className="hover:bg-slate-50">
                <td className="px-3 py-2 text-slate-700">{formatTimestamp(r.finished_at)}</td>
                <td className="px-3 py-2 text-slate-600">{r.triggered_by || '—'}</td>
                <td className="px-3 py-2 text-right">
                  <span className={ok ? 'text-emerald-700 font-semibold' : 'text-slate-700'}>
                    {s.passed ?? 0}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <span className={(s.failed || 0) > 0 ? 'text-rose-700 font-semibold' : 'text-slate-400'}>
                    {s.failed ?? 0}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <span className={(s.errored || 0) > 0 ? 'text-amber-700 font-semibold' : 'text-slate-400'}>
                    {s.errored ?? 0}
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-slate-500 text-xs">
                  {Math.round((r.duration_ms || 0) / 100) / 10}s
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function SecurityTestResultsPage() {
  const [latest, setLatest]   = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError]     = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const token = localStorage.getItem('fieldflow_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [latestR, histR] = await Promise.all([
        axios.get(`${API}/api/admin/security-tests/latest`, { headers }),
        axios.get(`${API}/api/admin/security-tests/history?limit=30`, { headers }),
      ]);
      setLatest(latestR.data);
      setHistory(histR.data.runs || []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load security test data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runNow = async () => {
    setRunning(true);
    setError('');
    const startedAt = new Date();
    try {
      const token = localStorage.getItem('fieldflow_token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/api/admin/security-tests/run`, {}, { headers });
      // Poll /latest until finished_at is newer than startedAt OR
      // ~45s elapses (the suite typically completes in 5-10s; rate-
      // limit test adds ~2s; cold start can push it to ~30s).
      const deadline = Date.now() + 45_000;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 1500));
        const r = await axios.get(`${API}/api/admin/security-tests/latest`, { headers });
        const finished = r.data?.finished_at ? new Date(r.data.finished_at) : null;
        if (finished && finished > startedAt) {
          setLatest(r.data);
          break;
        }
      }
      // Refresh history one final time
      const histR = await axios.get(`${API}/api/admin/security-tests/history?limit=30`, { headers });
      setHistory(histR.data.runs || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to run security tests');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="security-tests-page">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/admin/tools" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 gap-1 mb-2">
            <ChevronLeft className="h-4 w-4" /> Back to Admin Tools
          </Link>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-emerald-600" />
            Authorization Test Results
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Runtime authorization-boundary checks. Runs automatically every night at 3 AM EDT
            and can be triggered manually below. Each test verifies a specific role-permission
            contract — e.g. &ldquo;a technician cannot view another user&rsquo;s timesheets.&rdquo;
          </p>
        </div>
        <Button onClick={runNow} disabled={running} data-testid="security-tests-run-now-btn" className="gap-2">
          <RefreshCw className={`h-4 w-4 ${running ? 'animate-spin' : ''}`} />
          {running ? 'Running...' : 'Run Now'}
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="security-tests-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 italic">Loading…</div>
      ) : (
        <>
          <StatusBanner run={latest} />
          <Card>
            <CardContent className="p-5">
              <h2 className="text-base font-semibold text-slate-900 mb-3">
                {latest && latest.status !== 'never_run' ? 'Latest run · per-test results' : 'No results yet'}
              </h2>
              {latest && latest.tests && <TestList tests={latest.tests} />}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <h2 className="text-base font-semibold text-slate-900 mb-3">Run History · last 30</h2>
              <HistoryTable runs={history} />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
